import React, {
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react'
import authService from '@/services/authService'
import storage from '@/utils/storage'
import { AUTH_MAINTENANCE_ENABLED } from '@/config/maintenance'
import type { User, AuthToken, AuthContextType, ApiResponse } from '@/types'
import { AuthContext } from './auth-context'

const AUTH_USER_KEY = 'auth_user'

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider = ({
  children,
}: AuthProviderProps): React.JSX.Element => {
  const [user, setUser] = useState<User | null>(() => {
    if (AUTH_MAINTENANCE_ENABLED) {
      authService.clearLocalAuthSession()
      return null
    }
    return authService.getUser()
  })
  const [token, setToken] = useState<string | null>(() =>
    AUTH_MAINTENANCE_ENABLED ? null : authService.getToken()
  )
  const [isAuthenticated, setIsAuthenticated] = useState(
    () => !AUTH_MAINTENANCE_ENABLED && authService.isAuthenticated()
  )

  useEffect(() => {
    const handleStorageChange = () => {
      setUser(authService.getUser())
      setToken(authService.getToken())
      setIsAuthenticated(authService.isAuthenticated())
    }

    window.addEventListener('storage', handleStorageChange)
    return () => {
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [])

  const login = useCallback(
    async (params: {
      password: string
      email?: string
      customer_number?: string
      rememberMe?: boolean
    }): Promise<AuthToken> => {
      const result = await authService.login(params)
      setUser(result.user)
      setToken(result.token)
      setIsAuthenticated(true)
      return result
    },
    []
  )

  const setSession = useCallback((result: AuthToken): void => {
    setUser(result.user)
    setToken(result.token)
    setIsAuthenticated(true)
  }, [])

  const register = useCallback(
    async (userData: Record<string, unknown>): Promise<AuthToken> => {
      const result = await authService.register(userData)
      setUser(result.user)
      setToken(result.token)
      setIsAuthenticated(true)
      return result
    },
    []
  )

  const logout = useCallback((): void => {
    authService.logout()
    setUser(null)
    setToken(null)
    setIsAuthenticated(false)
  }, [])

  const updateProfile = useCallback(
    async (userData: Partial<User>): Promise<ApiResponse<User>> => {
      const result = await authService.updateProfile(userData)
      if (result && typeof result === 'object' && 'user' in result) {
        setUser((result as { user: User }).user)
      }
      return result as ApiResponse<User>
    },
    []
  )

  const refreshToken = useCallback(async (): Promise<ApiResponse<{ token: string }>> => {
    const result = await authService.refreshToken()
    if (result && typeof result === 'object') {
      // Update user data if present in response
      if ('data' in result && result.data && typeof result.data === 'object') {
        const userData = result.data as User
        setUser(userData)
        storage.setLocal(AUTH_USER_KEY, JSON.stringify(userData))
      }
      // Update token if present
      if ('token' in result) {
        setToken((result as { token: string }).token)
      }
    }
    return result as ApiResponse<{ token: string }>
  }, [])

  const value: AuthContextType = {
    user,
    isAuthenticated,
    token,
    login,
    setSession,
    register,
    logout,
    forgotPassword: authService.forgotPassword,
    resetPassword: authService.resetPassword,
    verifyEmail: authService.verifyEmail,
    refreshToken,
    updateProfile,
    changePassword: authService.changePassword.bind(authService),
    resendVerificationEmail:
      authService.resendVerificationEmail,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export default AuthProvider
